import {
  resolveConsentState,
  validateConsentDecision,
  type ConsentAction,
  type ConsentDataCategory,
  type ConsentEvent,
} from '../../domain/privacy/consent'
import { privacyPreferencesProvider } from '../../integrations/supabase/privacyPreferencesProvider'
import { mapProviderError, type AppResult } from '../platform/public'

export type ConsentPreference = Readonly<{
  purpose: string
  category: ConsentDataCategory
  policyVersion: number
  labelKey: string
  descriptionKey: string
  status: 'granted' | 'denied' | 'revoked' | 'missing'
  effectiveAt: string | null
}>

function correlationId(): string {
  return `consent-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

function reason(value: string): string {
  const normalized = value.trim()
  if (normalized.length < 2 || normalized.length > 1000) throw new Error('consent_reason_invalid')
  return normalized
}

export const privacyPreferencesApi = {
  async load(tenantId: string): Promise<AppResult<ConsentPreference[]>> {
    const result = await privacyPreferencesProvider.load(tenantId)
    if (result.error || !result.data) return { data: null, error: mapProviderError(result.error) }

    const events = result.data.events.map((row): ConsentEvent => ({
      workspaceId: String(row.tenant_id),
      userId: String(row.user_id),
      purpose: String(row.purpose),
      category: String(row.data_category) as ConsentDataCategory,
      policyVersion: Number(row.policy_version),
      action: String(row.action) as ConsentAction,
      effectiveAt: String(row.effective_at),
      createdAt: String(row.created_at),
    }))

    return {
      data: result.data.policies.map((row) => {
        const purpose = String(row.purpose)
        const category = String(row.data_category) as ConsentDataCategory
        const policyVersion = Number(row.policy_version)
        const state = resolveConsentState(events, purpose, category, policyVersion)
        return {
          purpose,
          category,
          policyVersion,
          labelKey: String(row.label_key),
          descriptionKey: String(row.description_key),
          status: state.status,
          effectiveAt: state.effectiveAt,
        }
      }),
      error: null,
    }
  },

  async decide(input: {
    tenantId: string
    purpose: string
    category: ConsentDataCategory
    policyVersion: number
    action: ConsentAction
    reason: string
  }): Promise<AppResult<null>> {
    try {
      const effectiveAt = new Date().toISOString()
      validateConsentDecision({
        purpose: input.purpose,
        category: input.category,
        policyVersion: input.policyVersion,
        action: input.action,
        effectiveAt,
      })
      const result = await privacyPreferencesProvider.decide({
        tenantId: input.tenantId,
        purpose: input.purpose,
        dataCategory: input.category,
        policyVersion: input.policyVersion,
        action: input.action,
        effectiveAt,
        reason: reason(input.reason),
        correlationId: correlationId(),
      })
      return { data: null, error: mapProviderError(result.error) }
    } catch (error) {
      return { data: null, error: { code: 'INVALID', message: error instanceof Error ? error.message : 'consent_request_invalid' } }
    }
  },
}
